import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { ScrollContainer } from "../techAssist/components";
import Grid from '@mui/material/Grid2';
import { useCalculatorConfig } from "../../fetch/fetchCalculatorConfig";
import { XmlParser } from "./xmlParser";
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@mui/material";
import { logCalculatorDownloadUsage, logCalculatorEventUsage, logCalculatorTimeOnPageUsage, logCalculatorUsage, logFeatureUsage } from "../../fetch/insightsHelper";
import { useDebounce } from "use-debounce";
import TimeOnPageTracker from "../TimeOnPageTracker";

import './calculator.css';

const sourceItemNumberIndex = 3;
const sourceQuantityIndex = 5;
const sourceDealerPriceIndex = 6;
const sourceMsrpIndex = 8;
const sourceLaborTimeIndex = 10;

const columnOrderWithDealerPrice = [
    'hour maintenance',
    'item number',
    'item description',
    'quantity',
    'dealer price',
    'dealer total',
    'msrp',
    'msrp total',
    'labor time (hours)',
    'shop rate',
    'labor total',
    'total maintenance price for customer'
];

const columnOrderWithoutDealerPrice = [
    'hour maintenance',
    'item number',
    'item description',
    'quantity',
    'msrp',
    'msrp total',
    'labor time (hours)',
    'shop rate',
    'labor total',
    'total maintenance price for customer'
];

export default function CalculatorPage() {
    const { engineType } = useParams();
    const engineTypeDisplay = engineType.toLowerCase() === 'outboard' ? 'Outboard' : 'MerCruiser';
    const [showDealerPrice, setShowDealerPrice] = useState(true);
    const [selectedModel, setSelectedModel] = useState('');
    const [selectedSerialGroup, setSelectedSerialGroup] = useState('');
    const [selectedHourMaintenance, setSelectedHourMaintenance] = useState('');
    const [shopRate, setShopRate] = useState(0);
    const hasRatesCalculator = useSelector((state) => state.permissions.hasRatesCalculator);
    const isPermissionsLoaded = useSelector((state) => state.permissions.isLoaded);
    const navigate = useNavigate();

    useEffect(() => {
        if (isPermissionsLoaded && !hasRatesCalculator) {
            navigate('/');
        }
    }, [isPermissionsLoaded, hasRatesCalculator]);

    const { data: xmlContent, error, isValidating, isLoading, mutate } = useCalculatorConfig(engineType);

    useEffect(() => {
        document.title = `Maintenance Schedule and Rates - ${engineTypeDisplay} - Mercury Marine`;
        logFeatureUsage(`Maintenance Schedule and Rates - ${engineTypeDisplay}`);
    }, [engineTypeDisplay]);


    const [debouncedInsights, callback] = useDebounce(JSON.stringify({
        engineType: engineTypeDisplay,
        model: selectedModel,
        serialGroup: selectedSerialGroup,
        hourMaintenance: selectedHourMaintenance,
        shopRate: shopRate?.toString(),
        hideDealer: !showDealerPrice
    }), 1000);

    useEffect(() => {
        if (!!engineTypeDisplay && !!selectedModel && !!selectedSerialGroup) {
            logCalculatorUsage({
                engineType: engineTypeDisplay,
                model: selectedModel,
                serialGroup: selectedSerialGroup,
                hourMaintenance: selectedHourMaintenance,
                shopRate: shopRate?.toString(),
                hideDealer: !showDealerPrice
            })
        }
    }, [debouncedInsights]);

    const xmlParser = useMemo(() => {
        if (!!xmlContent) {
            return new XmlParser(xmlContent);
        }
    }, [xmlContent]);

    const pageTitle = useMemo(() => {
        if (!!xmlParser) {
            return xmlParser.getData('app-chart/table/title');
        }

        return 'Maintenance Schedule and Rates';
    }, [xmlParser]);

    const tableData = useMemo(() => {
        if (!!xmlParser) {
            const headerRow = xmlParser.getData('app-chart/table/tgroup/thead/row')[0];
            const columnOrder = showDealerPrice ? columnOrderWithDealerPrice : columnOrderWithoutDealerPrice;
            const headerColumnsUnsorted = Array.from(headerRow.children).map((node, index) => ({ text: node.textContent.trim(), sourceIndex: index, targetIndex: (columnOrder).findIndex(column => column === node.textContent.toLowerCase().trim()), isItemNumber: index === sourceItemNumberIndex }));
            const headerColumns = [...headerColumnsUnsorted];
            headerColumns.sort((a, b) => a.targetIndex - b.targetIndex);
            const bodyRows = xmlParser.getData('app-chart/table/tgroup/tbody/row')

            const models = [...new Set(bodyRows.map(row => row.children[0].textContent))];

            const filteredByModel = bodyRows.filter(row => row.children[0].textContent === selectedModel);
            const filteredByModelAndSerial = filteredByModel.filter(row => row.children[1].textContent === selectedSerialGroup);
            const hourMaintenances = [...new Set(filteredByModelAndSerial.map(row => row.children[2].textContent))];

            let serialGroups = [];
            if (!!selectedModel) {
                serialGroups = [...new Set(filteredByModel.map(row => row.children[1].textContent))];
            }

            if (!!selectedModel && !!selectedSerialGroup) {
                serialGroups = [...new Set(filteredByModelAndSerial.map(row => row.children[1].textContent))];
            }

            const filteredByModelSerialAndHour = filteredByModelAndSerial.filter(row => row.children[2].textContent === selectedHourMaintenance);

            const itemNumberIndex = columnOrder.findIndex(column => column === 'item number');
            const quantityIndex = columnOrder.findIndex(column => column === 'quantity');
            const shopRateIndex = columnOrder.findIndex(column => column === 'shop rate');
            const laborTotalIndex = columnOrder.findIndex(column => column === 'labor total');
            const dealerTotalIndex = columnOrder.findIndex(column => column === 'dealer total');
            const msrpTotalIndex = columnOrder.findIndex(column => column === 'msrp total');
            const totalIndex = columnOrder.findIndex(column => column === 'total maintenance price for customer');


            const usdFormatter = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD'
            });

            const filteredRows = (!!selectedHourMaintenance ? filteredByModelSerialAndHour : filteredByModelAndSerial)
                .map(row => {
                    const rowChildren = Array.from(row.children);

                    const laborTimeValue = +rowChildren[sourceLaborTimeIndex].textContent;
                    const dealerPrices = rowChildren[sourceDealerPriceIndex].innerHTML.split('<linebrk/>');
                    const quantityValues = rowChildren[sourceQuantityIndex].innerHTML.split('<linebrk/>');
                    const msrpValues = rowChildren[sourceMsrpIndex].innerHTML.split('<linebrk/>');

                    const dealerSubTotal = dealerPrices.map((dealerPrice, index) => {
                        const quantity = +quantityValues[index] || 1;
                        const linePrice = +(dealerPrice.replace('$', '')) * quantity;
                        return linePrice;
                    });
                    const msrpSubTotal = msrpValues.map((msrp, index) => {
                        const quantity = +quantityValues[index] || 1;
                        const linePrice = +(msrp.replace('$', '')) * quantity;
                        return linePrice;
                    });
                    const laborTotal = laborTimeValue * shopRate;

                    const msrpTotal = msrpSubTotal.reduce((accumulator, currentValue) => accumulator + currentValue, 0);

                    const cells = rowChildren.map((node, sourceIndex) => {
                        const targetIndex = headerColumnsUnsorted[sourceIndex]?.targetIndex;
                        let text = node.innerHTML.replaceAll('<linebrk/>', '<br/>').replaceAll('\n\t\t\t\t\t\t', '').replaceAll('\n\t\t\t\t\t', '');
                        if (targetIndex === shopRateIndex) {
                            text = usdFormatter.format(shopRate);
                        } else if (targetIndex === laborTotalIndex) {
                            text = usdFormatter.format(laborTotal);
                        } else if (targetIndex === dealerTotalIndex) {
                            text = dealerSubTotal.map(value => {
                                if (value === 0) {
                                    return '';
                                }
                                return usdFormatter.format(value)
                            }).join('<br/>');
                        } else if (targetIndex === msrpTotalIndex) {
                            text = msrpSubTotal.map(value => {
                                if (value === 0) {
                                    return '';
                                }
                                return usdFormatter.format(value)
                            }).join('<br/>');
                        } else if (targetIndex === totalIndex) {
                            text = usdFormatter.format(laborTotal + msrpTotal);
                        } else if (targetIndex === itemNumberIndex) {
                            const itemNumbers = text.split('<br/>').map(item => {
                                return extractRawPartNumber(item);
                            }).join('<br/>');
                            text = itemNumbers;
                        }

                        return {
                            text,
                            sourceIndex,
                            targetIndex
                        };
                    });

                    cells.sort((a, b) => a.targetIndex - b.targetIndex);
                    return { cells };
                });

            const csvRows = filteredRows.map(row => {
                return row.cells.map((cell, index) => {
                    if (cell.targetIndex !== itemNumberIndex && cell.targetIndex !== quantityIndex) {
                        return null;
                    }

                    if (cell.targetIndex == itemNumberIndex) {
                        const itemNumbers = cell.text.split('<br/>').map(item => item.trim());
                        return itemNumbers;
                    }

                    if (cell.targetIndex == quantityIndex) {
                        const quantities = cell.text.split('<br/>').map(item => item.trim());
                        return quantities;
                    }

                    return cell.text
                }).filter(cell => cell !== null);
            }).map(row => {
                const rowData = [];
                for (let i = 0; i < row[0].length; i++) {
                    if (!!row[0][i]) {
                        let partNumber = row[0][i];
                        const quantity = row[1][i];
                        rowData.push([
                            partNumber,
                            quantity
                        ]);
                    }
                }
                return rowData;
            })[0] || [];

            csvRows.unshift(['INV_NUM', 'QTY']);

            return {
                headerColumns,
                models,
                serialGroups,
                hourMaintenances,
                bodyRows: filteredRows,
                csvRows
            }
        }

        return null;
    }, [xmlContent, xmlParser, showDealerPrice, selectedModel, selectedSerialGroup, selectedHourMaintenance, shopRate]);

    const handleDownload = () => {
        logCalculatorDownloadUsage({
            engineType: engineTypeDisplay,
            model: selectedModel,
            serialGroup: selectedSerialGroup,
            hourMaintenance: selectedHourMaintenance,
        });
        logCalculatorEventUsage({
            engineType: engineTypeDisplay,
            element: 'download'
        });
        const csvContent = "data:text/csv;charset=utf-8," + tableData.csvRows.map(e => e.join(",")).join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `${engineTypeDisplay}_maintenance_parts_list.csv`);
        document.body.appendChild(link); // Required for FF
        link.click();
        document.body.removeChild(link);
    };

    useEffect(() => {
        if (!!selectedModel && tableData?.serialGroups.length === 1) {
            setSelectedSerialGroup(tableData?.serialGroups[0]);
        }
    }, [selectedModel]);

    useEffect(() => {
        if (!!selectedSerialGroup && tableData?.hourMaintenances.length === 1) {
            setSelectedHourMaintenance(tableData?.hourMaintenances[0]);
        }
    }, [selectedSerialGroup]);


    const handleTimeSpent = (timeInSeconds, identifier) => {
        logCalculatorTimeOnPageUsage({
            engineType: engineTypeDisplay,
            seconds: timeInSeconds,
            identifier
        });
    };

    return (
        <ScrollContainer>
            <TimeOnPageTracker onTimeSpent={handleTimeSpent} />
            <Grid container spacing={2} className="calculatorPage">
                <Grid size={12}>
                    <h2>{pageTitle}</h2>

                    <div className="dropDowns">
                        <div className="label-select">
                            <label>Model</label>
                            <select id="model" title="Model" size="1" value={selectedModel}
                                onChange={(e) => {
                                    setSelectedModel(e.target.value);
                                    setSelectedSerialGroup('');
                                    setSelectedHourMaintenance('');
                                }}>
                                <option value=''>Select Model</option>
                                {tableData?.models.map((model, index) => (
                                    <option key={index} value={model}>{model}</option>
                                ))}
                            </select>
                            <span className='selected-value'>{selectedModel}</span>
                        </div>
                        <div className="label-select">
                            <label>Serial Group</label>
                            <select id="sn" title="Serial Range" size="1" value={selectedSerialGroup} onChange={(e) => setSelectedSerialGroup(e.target.value)}>
                                <option value=''>Select Serial Group</option>
                                {tableData?.serialGroups.map((serialGroup, index) => (
                                    <option key={index} value={serialGroup}>{serialGroup}</option>
                                ))}
                            </select>
                            <span className='selected-value'>{selectedSerialGroup}</span>
                        </div>
                        <div className="label-select">
                            <label>Hour Maintenance</label>
                            <select id="hour" title="Hour Maintenance" size="1" value={selectedHourMaintenance} onChange={(e) => setSelectedHourMaintenance(e.target.value)}>
                                <option value=''>Select Hour Maintenance</option>
                                {tableData?.hourMaintenances.map((hourMaintenance, index) => (
                                    <option key={index} value={hourMaintenance}>{hourMaintenance}</option>
                                ))}
                            </select>
                            <span className='selected-value'>{selectedHourMaintenance}</span>
                        </div>
                        <div className="shopRate label-select">
                            <label htmlFor="shopRate" title="Hourly shop rate">Shop Rate</label>
                            <span className="dollarSign">
                                $<input id="shopRate" type="search" value={shopRate} onChange={(e) => setShopRate(e.target.value)}
                                    title="Hourly shop rate in US dollars" min="0" incremental="incremental" />
                            </span>
                            <span className='selected-value'>${shopRate}</span>
                        </div>
                    </div>
                </Grid>
                <Grid size={12} className="action-buttons">
                    <Button variant="contained" onClick={() => {
                        setSelectedModel('');
                        setSelectedSerialGroup('');
                        setSelectedHourMaintenance('');
                        setShopRate(0);
                        logCalculatorEventUsage({
                            engineType: engineTypeDisplay,
                            element: 'reset'
                        });
                    }} style={{ marginRight: '0.5em' }}>Reset</Button>
                    <Button variant="contained" onClick={() => {
                        setShowDealerPrice(!showDealerPrice);

                        logCalculatorEventUsage({
                            engineType: engineTypeDisplay,
                            element: !showDealerPrice ? 'show dealer' : 'hide dealer'
                        });
                    }} style={{ marginRight: '0.5em' }}>{showDealerPrice ? 'Hide' : 'Show'} Dealer Price</Button>
                    {!!selectedModel && !!selectedSerialGroup && !!selectedHourMaintenance && (
                        <Button variant="contained" onClick={handleDownload} title="Download Parts List for MercNET Order Entry">
                            <img src="/download_icon_24.svg" style={{ height: '20px', marginRight: '0.5em' }} />
                            Download Parts List
                        </Button>
                    )}
                </Grid>
                <Grid size={12}>
                    <TableContainer>
                        <Table className={`parts ${showDealerPrice ? 'show_dealer' : 'hide_dealer'}`}>
                            <TableHead>
                                <TableRow>
                                    {tableData?.headerColumns.map((column, index) => {
                                        if (column.targetIndex !== -1) {
                                            const isDealerColumn = column.text.toLowerCase() === 'dealer price' || column.text.toLowerCase() === 'dealer total';
                                            return (
                                                <TableCell className={isDealerColumn ? 'dealer-column' : null} key={column.targetIndex} style={{ verticalAlign: 'top', backgroundColor: '#69a8a650' }}>{column.text}</TableCell>
                                            );
                                        }
                                        return null;
                                    })}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tableData?.bodyRows.map((row, rowIndex) => (
                                    <TableRow key={rowIndex}>
                                        {row.cells.map((cell, cellIndex) => {
                                            if (cell.targetIndex !== -1) {
                                                const isDealerColumn = tableData.headerColumns[cellIndex].text.toLowerCase() === 'dealer price' || tableData.headerColumns[cellIndex].text.toLowerCase() === 'dealer total';
                                                if (tableData.headerColumns[cellIndex].isItemNumber) {
                                                    var partNumbers = cell.text.split('<br/>');
                                                    return (
                                                        <TableCell key={cellIndex} style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                                                            {partNumbers.map((partNumber, index) => (
                                                                <React.Fragment key={index}>
                                                                    <Tooltip
                                                                        describeChild enterDelay={500} leaveDelay={200}
                                                                        title={
                                                                            <PartNumberTooltip engineTypeDisplay={engineTypeDisplay} partNumber={partNumber} />
                                                                        }>
                                                                        <Button variant="text" style={{ padding: 0, fontWeight: 'normal', marginTop: index === 0 ? '-2px' : '-6px' }}>{partNumber}</Button>
                                                                    </Tooltip>
                                                                    {index < partNumbers.length - 1 && <br />}
                                                                </React.Fragment>
                                                            ))}
                                                        </TableCell>
                                                    )
                                                }

                                                const lines = cell.text.split('<br/>');

                                                return (
                                                    <TableCell key={cellIndex} className={isDealerColumn ? 'dealer-column' : null} style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                                                        {lines.map((line, index) => (
                                                            <React.Fragment key={index}>
                                                                {line.replaceAll(' ', '\u00A0')}
                                                                {index < lines.length - 1 && <br />}
                                                            </React.Fragment>
                                                        ))}
                                                    </TableCell>
                                                )
                                            }
                                            return null;
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
                <Grid size={12}>
                    <p className="noteMessage">
                        <img className="noteMessageIcon" src="/information-round-svgrepo-com.svg" />All parts are subject to price in effect on the date of invoice. Data is subject to change without notice. <br />For the most current product and pricing information, please reference MercNET Availability.</p>
                    <Link onClick={() => {

                        logCalculatorEventUsage({
                            engineType: engineTypeDisplay,
                            element: engineType.toLowerCase() === 'outboard' ? 'click mercruiser' : 'click outboard'
                        });
                        navigate(engineType.toLowerCase() === 'outboard' ? '/literature/maintenance/calculator/mercruiser' : '/literature/maintenance/calculator/outboard');
                    }
                    }>{engineType.toLowerCase() === 'outboard' ? 'MerCruiser' : 'Outboard'} Maintenance Rates</Link>
                </Grid>
            </Grid>
        </ScrollContainer >
    );
}

function PartNumberTooltip({ engineTypeDisplay, partNumber }) {
    useEffect(() => {
        logCalculatorEventUsage({
            engineType: engineTypeDisplay,
            element: 'partNumberTooltip',
            parameter: partNumber
        });
    }, [partNumber]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <img
                src={`/api/v1/file/calc/images/${partNumber}.jpg`}
                alt={partNumber}
                style={{ maxWidth: '400px', maxHeight: '300px' }}
            />
        </Box>
    );
}

function extractRawPartNumber(input) {
    let partNumber = input.replace("</partno>", '');
    partNumber = partNumber.substring(partNumber.indexOf('>') + 1);
    return partNumber;
}